/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    IGroup,
    IGroups,
    IHeader,
    IList,
    IOrderHistoryViewProps,
    IOrderInformation,
    ISalesOrder
} from '@msdyn365-commerce-modules/order-management';
import { IMandsOrderHistoryProps } from './mands-order-history.props.autogenerated';
import { OrderShipments } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
export interface IOrderHistoryState {
    productInformation: (
        | {
              pId: string | undefined;
              pName: string | undefined;
              pQuantity: number | undefined;
          }[][]
        | undefined
    )[];
    count: number;
}
export interface IProductId {
    pId: string;
    pName: string;
    pQuantity: number;
}
export interface IOrderIndex {
    _index: number;
}
export interface IOrderStatus {
    SalesStatus: string | undefined;
}

export interface IOrderInfo {
    createdDateTime: string | undefined;
    orderID: string | undefined;
    orderTotal: string | 0 | undefined;
}
/**
 *
 * Order History Component
 * @extends {React.PureComponent<IOrderHistoryViewProps>}
 */

export class OrderHistoryView extends React.PureComponent<IOrderHistoryViewProps & IMandsOrderHistoryProps<{}>, IOrderHistoryState> {
    constructor(props: IOrderHistoryViewProps & IMandsOrderHistoryProps<{}>) {
        super(props);
        this.state = {
            productInformation: [],
            count: 0
        };
    }
    public render(): JSX.Element {
        const { orderHistoryProps, header, alert, loading, emptyMessage, backToShoppingLink, list } = this.props;
        return (
            <Module {...orderHistoryProps}>
                {header && <this.OrderHistoryHeader {...header} />}
                {this.renderHeadingRow()}
                {loading}
                {alert && (
                    <>
                        {alert}
                        {backToShoppingLink}
                    </>
                )}
                {emptyMessage && (
                    <>
                        {emptyMessage}
                        {backToShoppingLink}
                    </>
                )}
                {list && <this.OrderHistoryList {...list} />}
            </Module>
        );
    }
    public getOrderProducts = (salesOrders: ISalesOrder[]) => {
        return salesOrders.map(
            (salesOrder, index) =>
                salesOrder.groups &&
                salesOrder.groups.groups.map((group, j) =>
                    group.salesLines.map((salesLine, k) => {
                        return {
                            pId: salesLine.data.product?.ItemId ? salesLine.data.product.ItemId.toString() : '',
                            pName: salesLine.data.product?.Name ? salesLine.data.product.Name.toString() : '',
                            pQuantity: 0
                        };
                    })
                )
        );
    };
    public OrderHistoryList: React.FC<IList> = ({ listProps, salesOrders }) => {
        const prodInformation = this.getOrderProducts(salesOrders);
        prodInformation.map((prod, k) => {
            const products: IProductId[] = [];
            prod &&
                prod[0].map(item => {
                    const index = products.findIndex(y => y.pId === item.pId);
                    if (index !== -1) {
                        products[index].pQuantity = products[index].pQuantity ? (products[index].pQuantity += 1) : 0;
                    } else {
                        item.pQuantity = 1;
                        products.push(item);
                    }
                });
            if (prod && prod[0]) {
                prod[0] = products;
            }
        });
        this.setState(state => {
            if (state.count >= 3) {
                return null;
            }
            return { productInformation: prodInformation, count: state.count + 1 };
        });
        return (
            <Node {...listProps}>
                {salesOrders &&
                    salesOrders.map((salesOrder, index) => <this.OrderHistorySalesOder key={index} {...salesOrder} _index={index} />)}
            </Node>
        );
    };
    private renderHeadingRow = () => {
        const {
            config: { orderSummaryLabel, orderDescriptionLabel, orderStatusLabel, orderActionLabel }
        } = this.props;
        return (
            <div className='row'>
                <div className='col-md-3'>
                    <span>
                        <strong>{orderSummaryLabel}</strong>
                    </span>
                </div>
                <div className='col-md-5'>
                    <span>
                        <strong>{orderDescriptionLabel}</strong>
                    </span>
                </div>
                <div className='col-md-2'>
                    <span>
                        <strong>{orderStatusLabel}</strong>
                    </span>
                </div>
                <div className='col-md-2'>
                    <span>
                        <strong>{orderActionLabel}</strong>
                    </span>
                </div>
            </div>
        );
    };
    private getOrderInformation = (index: number) => {
        const salesOrder = this.props.orderHistory && this.props.orderHistory.salesOrders[index];
        let dateTime = salesOrder && salesOrder.CreatedDateTime && salesOrder.CreatedDateTime.toString();
        dateTime = dateTime && dateTime.substring(4, 21); // Mar 13 2020 20:30:18
        const salesID = salesOrder && salesOrder.ChannelReferenceId && salesOrder.ChannelReferenceId.toString();
        const totalAmount = salesOrder && salesOrder.TotalAmount && salesOrder.TotalAmount.toString();
        const orderInfo: IOrderInfo = {
            createdDateTime: dateTime,
            orderID: salesID,
            orderTotal: totalAmount
        };
        orderInfo;
        return orderInfo;
    };
    private OrderHistoryOrderInfomation: React.FC<IOrderInformation & IOrderIndex & IOrderStatus> = props => {
        const { orderInformationProps, _index, SalesStatus } = props;
        const {
            config: { confirmationIDLabel, orderTotalLabel },
            context: { cultureFormatter }
        } = this.props;
        const productInfo = this.state.productInformation;
        const currOrderProductsInfo = productInfo && productInfo[_index];
        const currentOrderProducts = currOrderProductsInfo && currOrderProductsInfo[0];
        const salesStatus =
            SalesStatus &&
            SalesStatus.split('')
                .map((word, index) => {
                    return index === 0 ? word.toUpperCase() : word;
                })
                .join('');
        const currentOrderInfo: IOrderInfo = this.getOrderInformation(_index);
        return (
            <>
                <div className='col-md-3'>
                    <Node {...orderInformationProps}>
                        <span className='ms-order-history__order-information-creation-date'>
                            <strong>{currentOrderInfo.createdDateTime}</strong>
                        </span>
                        <br />
                        <span className='msc-id ms-order-history__order-information-order-id'>
                            <span className='msc-id__order-id'>
                                {confirmationIDLabel}
                                <a>
                                    <u>{currentOrderInfo.orderID}</u>
                                </a>
                            </span>
                        </span>
                        <br />
                        <span className='msc-price ms-order-history__order-information-amount'>
                            <span className='msc-price__actual'>
                                {orderTotalLabel} {cultureFormatter.formatCurrency(Number(currentOrderInfo.orderTotal).toFixed(2))}
                            </span>
                        </span>
                        <br />
                    </Node>
                </div>
                <div className='col-md-5'>
                    <span className='msc-item ms-order-history__order-information-item'>
                        {currentOrderProducts &&
                            currentOrderProducts.map(product => (
                                <>
                                    <span className='msc-item__product'>
                                        {product.pName} {product.pQuantity && product.pQuantity > 1 ? `(${product.pQuantity} items) ` : ''}
                                    </span>
                                    <br />
                                </>
                            ))}
                    </span>
                </div>
                <div className='col-md-2'>
                    <span className='msc-status ms-order-history__order-status'>{salesStatus}</span>
                </div>
            </>
        );
    };
    private OrderHistoryGroup: React.FC<IGroup> = () => {
        return null;
    };
    private OrderHistoryGroups: React.FC<IGroups> = ({ groupsProps, groups }) => {
        return (
            <Node {...groupsProps}>
                {groups.map((group, index) => (
                    <this.OrderHistoryGroup key={index} {...group} />
                ))}
            </Node>
        );
    };
    private OrderHistoryHeader: React.FC<IHeader> = ({ headerProps, orderCountLabel }) => {
        const {
            config: { orderHeadingLabel }
        } = this.props;
        return (
            <Node {...headerProps}>
                <h2 className='ms-order-history__heading'>{orderHeadingLabel}</h2>
                {orderCountLabel}
            </Node>
        );
    };
    private OrderHistorySalesOder: React.FC<ISalesOrder & IOrderIndex> = props => {
        const { salesOrderProps, orderInfomation, groups, orderDetailsLink, _index } = props;
        const orderStatus = groups && groups.groups && groups.groups[0].data && groups.groups[0].data.salesStatus.toString();
        const {
            orderHistory: { salesOrders }
        } = this.props;
        let salesStatusCode: string | undefined = '';
        const salesID: string = salesOrderProps.key;
        let currentSalesOrder: OrderShipments | undefined;
        const currentSalesOrderIndex = salesOrders.findIndex(salesOrder => salesOrder.SalesId === salesID);

        if (currentSalesOrderIndex !== -1) {
            currentSalesOrder = salesOrders[currentSalesOrderIndex];
            currentSalesOrder.SalesLines?.some(salesLine => {
                const QuantityInvoiced = salesLine.QuantityInvoiced || 0;
                const QuantityOrdered = salesLine.QuantityOrdered || 0;
                if (QuantityInvoiced > 0 && QuantityInvoiced < QuantityOrdered) {
                    salesStatusCode = 'Partial Shipped';
                    return true;
                }
                return false;
            });

            !salesStatusCode &&
                currentSalesOrder.SalesLines?.some(salesLine => {
                    if (salesLine.SalesStatusValue === 4) {
                        salesStatusCode = 'Shipped';
                        return true;
                    }
                    return false;
                });
        }
        salesStatusCode = salesStatusCode ? salesStatusCode : orderStatus;
        console.log('order status <<>>', salesID, salesStatusCode);

        return (
            <Node {...salesOrderProps}>
                <div className='row'>
                    {orderInfomation && (
                        <this.OrderHistoryOrderInfomation {...orderInfomation} _index={_index} SalesStatus={salesStatusCode} />
                    )}
                    {groups && <this.OrderHistoryGroups {...groups} />}
                    <div className='col-md-2'>{orderDetailsLink}</div>
                </div>
            </Node>
        );
    };
}
export default OrderHistoryView;
